import type { CoursePageView } from "~/business-areas/course/course.model";
import type { ContactFlowMetadata } from "~~/model/analytics.model";

const ALERT_CREATION_QUERY_PARAMETER_KEY = "createAlert";
const ALERT_DELETION_QUERY_PARAMETER_KEY = "deleteAlert";

export function useCourseNewSessionAlert(course: CoursePageView) {
  const { t } = useI18n();
  const { $analytics } = useNuxtApp();
  const contactFlowMetadata: ContactFlowMetadata = {
    origin: "course",
    origin_page_action: "new_session_alert_cta",
    origin_page_id: course.slug,
  };

  const contactFormRedirect = `/courses/${course.slug}?${ALERT_CREATION_QUERY_PARAMETER_KEY}=true`;
  const route = useRoute();
  const { state } = useGrowthContact();
  const { deleteEntry, exists } = useGrowthContactFormHistory();

  async function createAlert({ email }: { email: string }) {
    try {
      await $fetch(`/api/courses/${course.id}/alerts`, {
        method: "POST",
        body: { email, course_id: course.id },
      });

      $analytics.track("course_new_session_alert_created");

      showSuccessToastNotification(
        t("course_session_alert.creation.success_message", { email }),
        {
          icon: "ph:bell-simple-ringing",
        },
      );
    } catch (error) {
      showErrorToastNotification((error as Error).message);
    }
  }

  function deleteAlert() {
    return withConfirmationDialog({
      title: `Supprimer l'alerte ?`,
      subtitle:
        "Vous ne serez plus notifié par email pour les prochaines sessions publiées.",
      onConfirm() {
        if (!state.value?.email || !hasAnAlert.value) return;

        deleteEntry({
          email: state.value.email,
          ...contactFlowMetadata,
        });

        $fetch(`/api/courses/${course.id}/alerts/${state.value.email}`, {
          method: "DELETE",
        });

        $analytics.track("course_new_session_alert_deleted");

        showInfoToastNotification(
          t("course_session_alert.deletion.success_message"),
          { icon: "ph:bell-simple-slash" },
        );
      },
    });
  }

  const hasAnAlert = computed(() => {
    return exists({
      email: state.value?.email ?? "",
      ...contactFlowMetadata,
    });
  });

  function handleQueryParameterBasedActions() {
    if (!import.meta.client || !state.value?.email) return;

    if (route.query[ALERT_CREATION_QUERY_PARAMETER_KEY] === "true") {
      createAlert({ email: state.value.email });
    }

    if (route.query[ALERT_DELETION_QUERY_PARAMETER_KEY] === "true") {
      // Have to wait for dialog handler to be available
      onNuxtReady(() => {
        deleteAlert();
      });
    }

    navigateTo({
      ...route,
      query: {
        ...route.query,
        [ALERT_CREATION_QUERY_PARAMETER_KEY]: undefined,
        [ALERT_DELETION_QUERY_PARAMETER_KEY]: undefined,
      },
    });
  }

  return {
    contactFlowMetadata,
    createAlert,
    deleteAlert,
    hasAnAlert,
    contactFormRedirect,
    handleQueryParameterBasedActions,
  };
}
